import React, { useEffect, useRef, useState } from 'react';
import { ParticipantSC } from './participant.styled';
import Video from 'twilio-video';
import LocalControls from '../local-controls';
import { useSelector } from 'react-redux';
import TStore from '../../../types/store';
import { useLocalControls } from '../local-controls/local-controls.utils';
import { TLocalDisplayMode } from '../../../types/twilio';
import { unSubscribe } from './participant.utils';

interface ParticipantProps {
  participant: Video.RemoteParticipant;
  type: 'local' | 'remote';
  displayMode: TLocalDisplayMode;
  name: string;
}

export const Participant: React.FunctionComponent<ParticipantProps> = ({
  participant,
  type,
  displayMode,
  name,
}) => {
  const [videoTracks, setVideoTracks] = useState<Video.RemoteTrack[]>([]);
  const [audioTracks, setAudioTracks] = useState<Video.RemoteTrack[]>([]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const muted = useSelector((state: TStore) => state.twilio.muted);

  const trackPubsToTracks = (
    trackMap:
      | Map<string, Video.RemoteVideoTrackPublication>
      | Map<string, Video.RemoteAudioTrackPublication>,
  ) =>
    Array.from(trackMap.values() as any)
      .map((publication) => (publication as any).track)
      .filter((track) => track !== null);

  useEffect(() => {
    const trackSubscribed = (track: Video.RemoteTrack) => {
      if (track.kind === 'video') {
        setVideoTracks((videoTracks) => [...videoTracks, track]);
      } else {
        setAudioTracks((audioTracks) => [...audioTracks, track]);
      }
    };

    const trackUnsubscribed = (track: Video.RemoteTrack) => {
      if (track.kind === 'video') {
        setVideoTracks((videoTracks) => videoTracks.filter((v) => v !== track));
      } else {
        setAudioTracks((audioTracks) => audioTracks.filter((a) => a !== track));
      }
    };

    setVideoTracks(trackPubsToTracks(participant.videoTracks));
    setAudioTracks(trackPubsToTracks(participant.audioTracks));

    participant.on('trackSubscribed', trackSubscribed);
    participant.on('trackUnsubscribed', trackUnsubscribed);
    participant.on('trackDisabled', (track: Video.RemoteTrack) => {
      if (type === 'remote') {
        if (track.kind === 'video') {
          setVideoTracks((videoTracks) => videoTracks.filter((v) => v !== track));
        } else {
          setAudioTracks((audioTracks) => audioTracks.filter((a) => a !== track));
        }
      }
    });

    participant.on('reconnecting', (track: Video.RemoteTrack) => {
      setVideoTracks((videoTracks) => videoTracks.filter((v) => v !== track));
      setAudioTracks((audioTracks) => audioTracks.filter((a) => a !== track));
    });

    return () => {
      setVideoTracks([]);
      setAudioTracks([]);
      participant.removeAllListeners();
    };
  }, [participant]);

  useLocalControls(participant, type);
  unSubscribe(videoTracks, videoRef, audioTracks, audioRef);

  return (
    <ParticipantSC type={type} displayMode={displayMode}>
      <div className={'video-container'}>
        <video ref={videoRef} autoPlay={true} />
        <div className={'overlay'} />
        <p className={'participant-name'}>{name}</p>
      </div>
      {type === 'remote' ? <audio ref={audioRef} autoPlay={true} muted={muted} /> : null}
      {type === 'remote' ? null : <LocalControls />}
    </ParticipantSC>
  );
};

export default React.memo(Participant);
